import { reportOperationError } from '../../../lib/observability.ts'
import { UserFacingError } from '../../../utils/UserFacingError.ts'
import { useCallback, useEffect, useRef, useState } from 'react'
import { createId, getLineSignature, isValidQuantity, roundQuantity } from '../../../lib/format'
import { calculateDiscountForLines } from '../../../lib/discounts'
import { buildSaleLine } from '../../catalog/services/saleLineBuilder'
import { buildRestaurantMenuBatchLines } from '../services/menuBatch'
import type { CatalogData, ResolvedCatalogItem, ResolvedSellableProduct } from '../../catalog/domain/types'
import type {
  AppliedDiscount,
  CashSession,
  Customer,
  PaymentMethod,
  ProductLineSelection,
  SaleCreatedPayload,
  TenantContext,
  TicketLine,
} from '../../../types'
import { nowIso } from '../../../utils/dates'
import { getReadableError } from '../../../utils/errors'
import { loadTicketNumberFromSupabase } from '../../../services/posService'
import {
  buildRestaurantPrintPayload,
  getEqualSplitPrintLines,
  getMovedRestaurantPrintLines,
  getRestaurantPrintSubtotal,
} from '../services/restaurantPrintPayload'
import { applyVenueLayout, restoreVenueTableLayout, saveVenueTableLayout } from '../../tables/layout-service'
import { getOrderPendingUnits } from '../../tables/service-status'
import {
  cancelEmptyRestaurantOrder,
  cleanupVirtualRoomRestaurantTable,
  closeRestaurantOrder,
  configureRestaurantEqualSplit,
  createVirtualRestaurantTable,
  deleteVirtualRestaurantTable,
  loadRestaurantEqualSplit,
  loadRestaurantOrder,
  loadRestaurantOrderGroup,
  loadRestaurantOrderPendingUnits,
  markRestaurantOrderFullyServed,
  markRestaurantOrderLineFullyServed,
  markRestaurantOrderLineUnitsServed,
  moveRestaurantOrder,
  moveRestaurantOrderLines,
  openRestaurantOrder,
  removeRestaurantOrderLineConfirmed,
  saveQuickSaleAsExistingTable,
  saveQuickSaleAsVirtualTable,
  saveRestaurantOrderLines,
} from '../../tables/service'
import { canDecreaseLineQuantity } from '../../tables/service-status'
import { loadTicketInvoice } from '../../customers/service'
import type {
  PayRestaurantEqualPartResult,
  PayRestaurantOrderItemsResult,
  PosView,
  RestaurantEqualSplit,
  RestaurantOrderDetail,
  RestaurantOrderGroupDetail,
  RestaurantOrderLineMove,
  RestaurantTableShape,
} from '../../tables/types'
import { useRestaurantDraft } from './useRestaurantDraft'
import { isRestaurantRevisionConflict, requiresConfirmedRestaurantLineRemoval, shouldSaveBeforeLeavingOrder } from '../draft-policy'
import { getRestaurantCashClosureError } from '../services/validateCashClosure'
import { useRestaurantRealtime } from './useRestaurantRealtime'
import {
  finishCashlogyPayment,
} from '../../local-printing/cashlogy/useCashlogyStore'
import type { CashlogyTransaction } from '../../local-printing/types'
import { requestEarlyCashDrawer } from '../../local-printing/services/earlyCashDrawer'
import { assertRealSaleAllowed } from '../../fiscal/local/mode.ts'
import { payRestaurantSale, recoverRestaurantPayments } from '../services/payment.ts'
import { usePrintAgentStore } from '../../local-printing/store/usePrintAgentStore'
import { PRINT_AGENT_ENABLED } from '../../local-printing/constants/config'
import { customerFiscalSnapshot } from '../../customers/customerValidation.ts'
import { hasTenantCapability } from '../../platform/tenantFeatureAccess'
import {
  loadOrderProductionState,
  sendProductionBatch,
  setOrderLineProductionPass,
  serveMenuComponent,
  subscribeToOrderProduction,
} from '../../production/service'
import type { OrderProductionState, ProductionSelection } from '../../production/types'
import { reconcileMenuEdit } from '../services/menuCourses'
import { isMenuOrderLine } from '../../tables/menu-service-model'

async function loadTicketNumberForPrint(context: TenantContext, cashSession: CashSession, ticketId: string) {
  const printState = usePrintAgentStore.getState()
  if (!PRINT_AGENT_ENABLED || !printState.token || !printState.selectedPrinterId) return undefined
  return loadTicketNumberFromSupabase(context, cashSession.id, ticketId)
}

type PendingPayment = {
  method: PaymentMethod | null
  receivedCents: number | null
  pendingUnits: number
  cashlogyTransaction?: CashlogyTransaction | null
  cashDrawerAlreadyRequested?: boolean
}

type Options = {
  appliedDiscount: AppliedDiscount | null
  catalog: CatalogData | null
  cashSession: CashSession | null
  context: TenantContext | null
  enabled: boolean
  isBusy: boolean
  isOnline: boolean
  onAddFeedback: (input: { feedbackType: 'added' | 'updated'; productName: string; sourceElement?: HTMLElement | null }) => void
  onError: (message: string | null) => void
  onPaidFeedback: (method: PaymentMethod | null) => void
  printSale: (payload: SaleCreatedPayload, options?: { cashDrawerAlreadyRequested?: boolean; fiscalMode?: 'legacy' | 'verifactu' }) => Promise<void>
  refreshCashSales: (ticketId: string, missingTicketTitle: string, shouldPrint?: boolean) => Promise<void>
  setAppliedDiscount: (discount: AppliedDiscount | null) => void
  setBusy: (busy: boolean) => void
  setMobileTicketOpen: (open: boolean) => void
  syncPendingEvents: () => Promise<void>
}

function withCalculationLines(
  discount: AppliedDiscount | null,
  lines: Array<{
    productId: string | null
    variantId: string | null
    unitPriceCents: number
    quantity: number
  }>,
) {
  return discount ? {
    ...discount,
    calculationLines: lines.map((line) => ({
      productId: line.productId ?? '',
      variantId: line.variantId,
      grossCents: line.unitPriceCents * line.quantity,
      quantity: line.quantity,
    })),
  } : null
}

function getVirtualRoomTable(detail: RestaurantOrderDetail) {
  return detail.tables.find((table) => table.isVirtual && table.areaId.startsWith('virtual:')) ?? null
}

function selectionContainsAllOrderLines(lines: RestaurantOrderDetail['lines'], moves: RestaurantOrderLineMove[]) {
  if (lines.length === 0) return false
  const selectedQuantities = new Map<string, number>()
  for (const move of moves) {
    selectedQuantities.set(move.lineId, (selectedQuantities.get(move.lineId) ?? 0) + move.quantity)
  }
  return lines.every((line) => (selectedQuantities.get(line.id) ?? 0) >= line.quantity)
}

export function useRestaurantController(options: Options) {
  const cashSessionId = options.cashSession?.id
  const deviceId = options.context?.deviceId
  const context = options.context
  const isOnline = options.isOnline
  const onError = options.onError
  const reportError = onError
  const paymentLockRef = useRef(false)
  const recoveryScopeRef = useRef<string | null>(null)
  useEffect(() => {
    const { context, cashSession } = options
    if (!options.enabled || !context || !cashSession) { recoveryScopeRef.current = null; return }
    if (!options.isOnline || !context.canTakePayments) return
    const scope = `${context.tenantId}:${context.venueId}:${context.deviceId}:${context.userId}:${cashSession.id}`
    if (recoveryScopeRef.current === scope) return
    recoveryScopeRef.current = scope
    void recoverRestaurantPayments(context, cashSession).then(async recovered => {
      for (const payment of recovered) {
        finishCashlogyPayment(payment.transaction)
        if (payment.result.ticketId) await options.refreshCashSales(payment.result.ticketId, 'Cobro recuperado; revisa el histórico', false)
      }
    }).catch(error => {
      reportOperationError(error, { operation: 'restaurant.action', step: 'restore_payment', recoverable: true })
      options.onError('Hay un cobro pendiente de recuperar. Conserva el almacenamiento y vuelve a abrir esta caja para reintentar; no vuelvas a cobrarlo.')
    })
  }, [options])
  const paymentScope = `${context?.tenantId}:${context?.venueId}:${context?.deviceId}:${context?.userId}:${cashSessionId}`
  const paymentScopeRef = useRef(paymentScope)
  paymentScopeRef.current = paymentScope
  const [posView, setPosView] = useState<PosView>({ type: 'quick_sale' })
  const [moveOrderId, setMoveOrderId] = useState<string | null>(null)
  const [pendingPayment, setPendingPayment] = useState<PendingPayment | null>(null)
  const [paymentProcessing, setPaymentProcessing] = useState(false)
  const [invoiceCustomer, setInvoiceCustomer] = useState<Customer | null>(null)
  const [pendingLineRemoval, setPendingLineRemoval] = useState<RestaurantOrderDetail['lines'][number] | null>(null)
  const [splitOrderGroup, setSplitOrderGroup] = useState<RestaurantOrderGroupDetail | null>(null)
  const [equalSplitOpen, setEqualSplitOpen] = useState(false)
  const [equalSplit, setEqualSplit] = useState<RestaurantEqualSplit | null>(null)
  const [productionState, setProductionState] = useState<OrderProductionState | null>(null)
  const draft = useRestaurantDraft({
    context: options.context,
    isOnline: options.isOnline,
    onError: (message) => options.onError(message),
  })
  const invoiceOrderId = posView.type === 'table_order' ? posView.orderId : null
  useEffect(() => {
    setInvoiceCustomer(null)
  }, [invoiceOrderId])

  const productionAvailable = Boolean(
    options.context
    && options.context.deviceMode !== 'kds'
    && hasTenantCapability(options.context, 'production'),
  )
  const orderServiceAvailable = productionAvailable || Boolean(draft.order?.lines.some(isMenuOrderLine))
  const refreshProduction = useCallback(async (orderId = invoiceOrderId) => {
    if (!orderServiceAvailable || !isOnline || !orderId) {
      setProductionState(null)
      return
    }
    setProductionState(await loadOrderProductionState(orderId))
  }, [invoiceOrderId, isOnline, orderServiceAvailable])

  useEffect(() => {
    if (!context || !isOnline || !invoiceOrderId || !orderServiceAvailable) {
      setProductionState(null)
      return undefined
    }
    void refreshProduction(invoiceOrderId).catch((cause) => onError(getReadableError(cause, { operation: 'restaurant.action', cashSessionId, operationId: invoiceOrderId })))
    return subscribeToOrderProduction(context, invoiceOrderId, () => {
      void refreshProduction(invoiceOrderId).catch((error) => reportOperationError(error, { operation: 'restaurant.refresh', recoverable: true }))
    })
  }, [cashSessionId, context, invoiceOrderId, isOnline, onError, orderServiceAvailable, refreshProduction])

  const replaceDraftOrder = draft.replaceOrder
  useEffect(() => {
    if (options.enabled) return
    setPosView({ type: 'quick_sale' })
    setMoveOrderId(null)
    setPendingPayment(null)
    setPendingLineRemoval(null)
    setSplitOrderGroup(null)
    setEqualSplitOpen(false)
    setEqualSplit(null)
    replaceDraftOrder(null)
  }, [options.enabled, replaceDraftOrder])
  const realtime = useRestaurantRealtime({
    activeCashSessionId: options.cashSession?.id,
    context: options.context,
    enabled: options.enabled,
    equalSplitOpen,
    isOnline: options.isOnline,
    onError: (message) => options.onError(message),
    posView,
    replaceOrder: draft.replaceOrder,
    saveState: draft.saveState,
    setEqualSplit,
    setPosView,
    setSplitOrderGroup,
    splitOrderGroup,
  })

  const refreshState = useCallback(async (orderId?: string) => {
    if (!options.context || !options.isOnline) return
    const [nextMap, nextOrder] = await Promise.all([
      realtime.loadCurrentMap(options.context),
      orderId ? loadRestaurantOrder(options.context, orderId) : Promise.resolve(null),
    ])
    realtime.setMap(nextMap)
    if (nextOrder) draft.replaceOrder(nextOrder)
  }, [draft, options.context, options.isOnline, realtime])

  const cleanupVirtualRoomTable = useCallback(async (detail: RestaurantOrderDetail, closeAsPaid: boolean) => {
    const table = getVirtualRoomTable(detail)
    if (!cashSessionId || !deviceId || !table) return null
    try {
      const removed = await cleanupVirtualRoomRestaurantTable({ cashSessionId, deviceId, tableId: table.id, closeAsPaid })
      return removed ? table.areaId : null
    } catch (error) {
      reportError(`La comanda se ha actualizado, pero no se pudo retirar la mesa de la sala Virtual: ${getReadableError(error, { operation: 'restaurant.action', cashSessionId: options.cashSession?.id, operationId: invoiceOrderId, step: 'cleanupVirtualRoomTable' })}`)
      return null
    }
  }, [cashSessionId, deviceId, reportError, invoiceOrderId, options.cashSession?.id])

  const runBusy = useCallback(async (action: () => Promise<void>) => {
    if (options.isBusy) return
    options.setBusy(true)
    options.onError(null)
    try {
      await action()
    } catch (error) {
      options.onError(getReadableError(error, { operation: 'restaurant.action', cashSessionId: options.cashSession?.id, operationId: invoiceOrderId, step: 'runBusy' }))
    } finally {
      options.setBusy(false)
    }
  }, [options, invoiceOrderId])

  const openTableOrder = useCallback((tableIds: string[], guestCount: number) => runBusy(async () => {
    if (!options.context?.canTakeOrders || !options.cashSession || !options.isOnline) return
    await options.syncPendingEvents()
    const orderId = await openRestaurantOrder({
      tableIds,
      guestCount,
      cashSessionId: options.cashSession.id,
      deviceId: options.context.deviceId,
    }).catch(async (error: unknown) => {
      if (/mesas? ya no est[áa] disponible/i.test(error && typeof error === 'object' && 'message' in error ? String(error.message) : '')) {
        // Keep the RPC error even if the map cannot be reloaded (e.g. offline).
        await realtime.refreshMap().catch((error) => reportOperationError(error, { operation: 'restaurant.refresh', recoverable: true }))
      }
      throw error
    })
    await refreshState(orderId)
    options.setAppliedDiscount(null)
    setPosView({ type: 'table_order', orderId })
  }), [options, realtime, refreshState, runBusy])

  const createVirtualTable = useCallback(async (input: { areaId: string | null; name: string; capacity: number; shape: RestaurantTableShape }) => {
    if (!options.context?.canTakeOrders || !options.cashSession || !options.isOnline || options.isBusy) return false
    options.setBusy(true)
    options.onError(null)
    try {
      await createVirtualRestaurantTable({
        ...input,
        cashSessionId: options.cashSession.id,
        deviceId: options.context.deviceId,
      })
      await realtime.refreshMap()
      setPosView({
        type: 'table_map',
        areaId: input.areaId ?? `virtual:${options.context.venueId}`,
      })
      return true
    } catch (error) {
      options.onError(getReadableError(error, { operation: 'restaurant.action', cashSessionId: options.cashSession?.id, operationId: invoiceOrderId, step: 'createVirtualTable' }))
      return false
    } finally {
      options.setBusy(false)
    }
  }, [options, realtime, invoiceOrderId])

  const createVirtualTableFromQuickSale = useCallback(async (
    input: { areaId: string | null; name: string; capacity: number; shape: RestaurantTableShape },
    lines: TicketLine[],
    discount: AppliedDiscount | null,
  ) => {
    if (!options.context?.canTakeOrders || !options.cashSession || !options.isOnline || options.isBusy || lines.length === 0) return false
    options.setBusy(true)
    options.onError(null)
    try {
      await saveQuickSaleAsVirtualTable({
        ...input,
        cashSessionId: options.cashSession.id,
        deviceId: options.context.deviceId,
        lines,
        discount,
      })
    } catch (error) {
      options.onError(getReadableError(error, { operation: 'restaurant.action', cashSessionId: options.cashSession?.id, operationId: invoiceOrderId, step: 'createVirtualTableFromQuickSale' }))
      options.setBusy(false)
      return false
    }

    options.setAppliedDiscount(null)
    draft.clearOrder()
    setPosView({ type: 'table_map', areaId: input.areaId ?? `virtual:${options.context.venueId}` })
    try {
      const nextMap = await realtime.loadCurrentMap(options.context, options.cashSession.id)
      realtime.setMap(nextMap)
    } catch (error) {
      options.onError(`La mesa se ha guardado, pero el mapa no se ha podido actualizar: ${getReadableError(error, { operation: 'restaurant.action', cashSessionId: options.cashSession?.id, operationId: invoiceOrderId, step: 'createVirtualTableFromQuickSale' })}`)
    } finally {
      options.setBusy(false)
    }
    return true
  }, [draft, options, realtime, invoiceOrderId])

  const saveQuickSaleToExistingTable = useCallback(async (
    tableId: string,
    lines: TicketLine[],
    discount: AppliedDiscount | null,
  ) => {
    if (!options.context?.canTakeOrders || !options.cashSession || !options.isOnline || options.isBusy || lines.length === 0) return false
    options.setBusy(true)
    options.onError(null)
    try {
      await saveQuickSaleAsExistingTable({
        cashSessionId: options.cashSession.id,
        deviceId: options.context.deviceId,
        tableId,
        lines,
        discount,
      })
    } catch (error) {
      options.onError(getReadableError(error, { operation: 'restaurant.action', cashSessionId: options.cashSession?.id, operationId: invoiceOrderId, step: 'saveQuickSaleToExistingTable' }))
      options.setBusy(false)
      return false
    }

    try {
      const nextMap = await realtime.loadCurrentMap(options.context, options.cashSession.id)
      realtime.setMap(nextMap)
    } catch (error) {
      options.onError(`La comanda se ha guardado, pero el mapa no se ha podido actualizar: ${getReadableError(error, { operation: 'restaurant.action', cashSessionId: options.cashSession?.id, operationId: invoiceOrderId, step: 'saveQuickSaleToExistingTable' })}`)
    } finally {
      options.setBusy(false)
    }
    return true
  }, [options, realtime, invoiceOrderId])

  const deleteVirtualTable = useCallback(async (tableId: string) => {
    if (!options.context?.canTakeOrders || !options.cashSession || !options.isOnline || options.isBusy) return false
    options.setBusy(true)
    options.onError(null)
    try {
      await deleteVirtualRestaurantTable({
        cashSessionId: options.cashSession.id,
        deviceId: options.context.deviceId,
        tableId,
      })
      const nextMap = await realtime.loadCurrentMap(options.context, options.cashSession.id)
      realtime.setMap(nextMap)
      return true
    } catch (error) {
      options.onError(getReadableError(error, { operation: 'restaurant.action', cashSessionId: options.cashSession?.id, operationId: invoiceOrderId, step: 'deleteVirtualTable' }))
      return false
    } finally {
      options.setBusy(false)
    }
  }, [options, realtime, invoiceOrderId])

  const openExistingOrder = useCallback((orderId: string) => runBusy(async () => {
    if (!options.context || !options.isOnline) return
    const [detail, activeSplit] = await Promise.all([
      loadRestaurantOrder(options.context, orderId),
      loadRestaurantEqualSplit(options.context, orderId),
    ])
    if (detail.order.status === 'carried_forward') throw new UserFacingError('Recupera primero las mesas pendientes del turno anterior.')
    draft.replaceOrder(detail)
    options.setAppliedDiscount(detail.order.draftDiscount)
    setPosView({ type: 'table_order', orderId })
    if (activeSplit?.paidParts) {
      setEqualSplit(activeSplit)
      setEqualSplitOpen(true)
    }
  }), [draft, options, runBusy])

  const returnToMap = useCallback(async () => {
    if (shouldSaveBeforeLeavingOrder(posView, draft.saveState) && !await draft.flush()) return
    try {
      const nextMap = options.context && options.isOnline
        ? await realtime.loadCurrentMap(options.context)
        : realtime.map
      options.setAppliedDiscount(null)
      draft.clearOrder()
      realtime.setMap(nextMap)
      setPosView({ type: 'table_map', areaId: nextMap.areas[0]?.id })
    } catch (error) {
      options.onError(getReadableError(error, { operation: 'restaurant.action', cashSessionId: options.cashSession?.id, operationId: invoiceOrderId, step: 'returnToMap' }))
    }
  }, [draft, options, posView, realtime, invoiceOrderId])

  const cancelEmptyOrder = useCallback(() => runBusy(async () => {
    const current = draft.getCurrentOrder()
    if (!options.context || !options.isOnline || !current || current.lines.length > 0) return
    const saved = await draft.flush()
    if (!saved || saved.lines.length > 0) return
    const areaId = saved.tables[0]?.areaId
    await cancelEmptyRestaurantOrder(saved.order.id, saved.order.revision)
    const cleanedAreaId = await cleanupVirtualRoomTable(saved, false)
    const nextMap = await realtime.loadCurrentMap(options.context)
    options.setAppliedDiscount(null)
    draft.clearOrder()
    setEqualSplitOpen(false)
    setEqualSplit(null)
    realtime.setMap(nextMap)
    setPosView({ type: 'table_map', areaId: cleanedAreaId ?? areaId ?? nextMap.areas[0]?.id })
  }), [cleanupVirtualRoomTable, draft, options, realtime, runBusy])

  const prepareMove = useCallback(async () => {
    const current = draft.getCurrentOrder()
    if (!current) return
    const saved = await draft.flush()
    if (!saved) return
    setMoveOrderId(saved.order.id)
    setPosView({ type: 'table_map', areaId: saved.tables[0]?.areaId })
  }, [draft])

  const moveOrder = useCallback((tableId: string) => runBusy(async () => {
    if (!moveOrderId || !options.isOnline) return
    const sourceOrder = draft.getCurrentOrder()
    await moveRestaurantOrder(moveOrderId, tableId)
    if (sourceOrder) await cleanupVirtualRoomTable(sourceOrder, false)
    await refreshState(moveOrderId)
    setPosView({ type: 'table_order', orderId: moveOrderId })
    setMoveOrderId(null)
  }), [cleanupVirtualRoomTable, draft, moveOrderId, options.isOnline, refreshState, runBusy])

  const removeLine = useCallback((lineId: string, confirmed: boolean) => runBusy(async () => {
    if (!options.context || !options.isOnline) return
    const saved = await draft.flush()
    if (!saved) return
    const currentLine = saved.lines.find((candidate) => candidate.id === lineId)
    if (!currentLine) {
      setPendingLineRemoval(null)
      return
    }
    const authoritativeProduction = productionAvailable ? await loadOrderProductionState(saved.order.id) : productionState
    const servedQuantity = Math.max(currentLine.servedQuantity, ...(authoritativeProduction?.entries.filter((entry) => entry.lineId === lineId).map((entry) => entry.servedQuantity ?? 0) ?? []))
    if (requiresConfirmedRestaurantLineRemoval(servedQuantity) && !confirmed) {
      setPendingLineRemoval(currentLine)
      return
    }
    try {
      if (confirmed || requiresConfirmedRestaurantLineRemoval(servedQuantity)) {
        await removeRestaurantOrderLineConfirmed(currentLine.id, saved.order.revision)
      } else {
        await saveRestaurantOrderLines({
          ...saved,
          lines: saved.lines.filter((candidate) => candidate.id !== currentLine.id),
        })
      }
      const cleanedAreaId = saved.lines.length === 1
        ? await cleanupVirtualRoomTable(saved, false)
        : null
      if (cleanedAreaId) {
        const nextMap = await realtime.loadCurrentMap(options.context, options.cashSession?.id)
        realtime.setMap(nextMap)
        draft.clearOrder()
        options.setAppliedDiscount(null)
        options.setMobileTicketOpen(false)
        setPendingLineRemoval(null)
        setPosView({ type: 'table_map', areaId: cleanedAreaId })
        return
      }
      draft.replaceOrder(await loadRestaurantOrder(options.context, saved.order.id))
      setPendingLineRemoval(null)
    } catch (error) {
      if (!isRestaurantRevisionConflict(error)) throw error
      draft.replaceOrder(await loadRestaurantOrder(options.context, saved.order.id))
      setPendingLineRemoval(null)
      options.onError('La comanda cambió en otro dispositivo. Se ha recargado la versión más reciente.')
    }
  }), [cleanupVirtualRoomTable, draft, options, productionAvailable, productionState, realtime, runBusy])

  const requestLineRemoval = useCallback((lineId: string) => {
    const line = draft.getCurrentOrder()?.lines.find((candidate) => candidate.id === lineId)
    if (!line || !options.isOnline) return
    const servedQuantity = Math.max(line.servedQuantity, ...(productionState?.entries.filter((entry) => entry.lineId === lineId).map((entry) => entry.servedQuantity ?? 0) ?? []))
    if (requiresConfirmedRestaurantLineRemoval(servedQuantity)) {
      setPendingLineRemoval(line)
      return
    }
    void removeLine(line.id, false)
  }, [draft, options.isOnline, productionState, removeLine])

  const confirmLineRemoval = useCallback(() => {
    if (!pendingLineRemoval) return
    void removeLine(pendingLineRemoval.id, true)
  }, [pendingLineRemoval, removeLine])

  const openSplitOrder = useCallback(() => runBusy(async () => {
    if (!options.context || !options.isOnline || !draft.getCurrentOrder()) return
    const saved = await draft.flush()
    if (saved) setSplitOrderGroup(await loadRestaurantOrderGroup(options.context, saved.order.id))
  }), [draft, options, runBusy])

  const openEqualSplitOrder = useCallback(() => runBusy(async () => {
    if (!options.context || !options.isOnline || !draft.getCurrentOrder()) return
    const saved = await draft.flush()
    if (!saved) return
    draft.replaceOrder(saved)
    setEqualSplit(await loadRestaurantEqualSplit(options.context, saved.order.id))
    setEqualSplitOpen(true)
  }), [draft, options, runBusy])

  const configureEqualSplit = useCallback(async (partCount: number) => {
    const current = draft.getCurrentOrder()
    if (!current) throw new UserFacingError('No hay una comanda abierta.')
    options.setBusy(true)
    options.onError(null)
    try {
      const configured = await configureRestaurantEqualSplit(
        current.order.id,
        partCount,
        current.order.revision,
        withCalculationLines(options.appliedDiscount, current.lines),
      )
      setEqualSplit(configured)
      return configured
    } catch (error) {
      options.onError(getReadableError(error, { operation: 'restaurant.action', cashSessionId: options.cashSession?.id, operationId: invoiceOrderId, step: 'configureEqualSplit' }))
      throw error
    } finally {
      options.setBusy(false)
    }
  }, [draft, options, invoiceOrderId])

  const refreshSales = useCallback(async (ticketId: string, missingTicketTitle: string, shouldPrint = true) => {
    await options.refreshCashSales(ticketId, missingTicketTitle, shouldPrint)
  }, [options])

  const payEqualSplitPart = useCallback(async (
    method: PaymentMethod | null,
    receivedCents: number | null,
    allowPending: boolean,
    discount: AppliedDiscount | null,
    useDefaultDiscount: boolean,
  ): Promise<PayRestaurantEqualPartResult> => {
    assertRealSaleAllowed()
    const current = draft.getCurrentOrder()
    if (!options.context || !options.cashSession || !equalSplit || !current) throw new UserFacingError('No hay una división activa.')
    if (paymentLockRef.current) throw new UserFacingError('Ya hay un cobro en curso.')
    paymentLockRef.current = true
    options.setBusy(true)
    options.onError(null)
    let committedResult: PayRestaurantEqualPartResult | null = null
    try {
      const paymentLines = getEqualSplitPrintLines(current.lines, equalSplit)
      if (!allowPending) {
        const pending = await loadRestaurantOrderPendingUnits(options.context, current.order.id)
        draft.replaceOrder(pending.detail)
        if (pending.pendingUnits > 0) return { requiresConfirmation: true, pendingUnits: pending.pendingUnits, split: equalSplit }
      }
      const effectiveDiscount = useDefaultDiscount ? equalSplit.nextDefaultDiscount : discount
      const amountCents = useDefaultDiscount
        ? equalSplit.nextDefaultTotalCents
        : calculateDiscountForLines(paymentLines.map((line) => ({
            productId: line.productId ?? '', variantId: line.variantId ?? '', grossCents: line.lineTotalCents ?? line.unitPriceCents * line.quantity, quantity: line.quantity,
          })), effectiveDiscount).totalCents
      const singleTicket = equalSplit.singleTicket === true
      const finalPart = singleTicket && equalSplit.remainingParts === 1
      const totalCents = finalPart ? (equalSplit.tenders ?? []).reduce((sum, tender) => sum + tender.amountCents, 0) + amountCents : amountCents
      const subtotalCents = singleTicket
        ? finalPart ? equalSplit.totalCents : equalSplit.nextPartCents
        : getRestaurantPrintSubtotal(paymentLines)
      const totalDiscountCents = subtotalCents - totalCents
      const documentDiscount: AppliedDiscount | null = finalPart
        ? totalDiscountCents > 0 ? { discountId: null, name: 'Descuentos por comensal', type: 'manual', calculationType: 'fixed',
          value: totalDiscountCents, fixedApplication: 'ticket', roundingIncrementCents: null, color: null } : null
        : effectiveDiscount
      // Intermediate shares reserve money only. Recalculating discounts against
      // fractional product quantities can reject the second share; the final bill
      // snapshots the original lines and the accumulated discount once.
      const documentLines = singleTicket ? finalPart ? current.lines : [] : paymentLines
      const paymentDraft = buildRestaurantPrintPayload({ cashSession: options.cashSession, context: options.context, createdAt: nowIso(),
        discount: singleTicket && !finalPart ? null : documentDiscount, lines: documentLines, paymentId: createId(), paymentMethod: method,
        receivedCents, changeCents: null, saleId: createId(), subtotalCents, ticketId: createId(), totalCents })
      if (singleTicket && !finalPart) paymentDraft.ticket.discount = documentDiscount
      // The final document covers the full order; hardware collects only this person's share.
      if (singleTicket && paymentDraft.payment) paymentDraft.payment = { ...paymentDraft.payment, amountCents,
        changeCents: method === 'cash' ? Math.max(0, (receivedCents ?? amountCents) - amountCents) : 0 }
      const payment = await payRestaurantSale<PayRestaurantEqualPartResult>({
        context: options.context, session: options.cashSession, action: 'equal_part', target: equalSplit.id, sequence: equalSplit.paidParts,
        draft: paymentDraft,
        params: { splitId: equalSplit.id, expectedPaidParts: equalSplit.paidParts, method, receivedCents, allowPending,
          ...(singleTicket ? { singleTicket, finalPart, amountCents } : {}),
          discount: withCalculationLines(discount, paymentLines), useDefaultDiscount },
      })
      const result = payment.result
      if (!result.requiresConfirmation) committedResult = result
      const cashlogy = payment
      const prepared = { payload: payment.payload }
      const production = Boolean(payment.payload?.localFiscal)
      const cashDrawerAlreadyRequested = !result.requiresConfirmation && requestEarlyCashDrawer({ requestId: 'drawer:' + (result.saleId ?? `${equalSplit.id}:${equalSplit.paidParts}`) + ':payment', payments: [{ method, amountCents }] })
      setEqualSplit(result.split)
      if (!result.requiresConfirmation) {
        if (singleTicket && !result.completed) {
          finishCashlogyPayment(cashlogy.transaction)
          realtime.setMap(await realtime.loadCurrentMap(options.context, options.cashSession.id))
          return result
        }
        if (!result.ticketId || !result.saleId) throw new Error('Falta la identidad de la venta completada.')
        if (production && !prepared?.payload) throw new Error('Falta la factura fiscal del cobro confirmado.')
        finishCashlogyPayment(cashlogy.transaction)
        const cleanedAreaId = result.completed
          ? await cleanupVirtualRoomTable(current, true)
          : null
        const printLines = paymentLines
        const [fiscal, ticketNumber] = await Promise.all([
          Promise.resolve(undefined),
          loadTicketNumberForPrint(options.context, options.cashSession, result.ticketId),
        ])
        void options.printSale(prepared?.payload ? { ...prepared.payload, ticket: { ...prepared.payload.ticket, ticketNumber } } : buildRestaurantPrintPayload({
          cashSession: options.cashSession,
          context: options.context,
          createdAt: nowIso(),
          discount: useDefaultDiscount ? equalSplit.nextDefaultDiscount : discount,
          lines: printLines,
          paymentId: result.paymentId,
          paymentMethod: method,
          receivedCents: cashlogy.receivedCents,
          changeCents: cashlogy.changeCents,
          saleId: result.saleId,
          subtotalCents: getRestaurantPrintSubtotal(printLines),
          ticketId: result.ticketId,
          ticketNumber,
          totalCents: result.paidAmountCents,
          fiscal,
        }), { cashDrawerAlreadyRequested: cashDrawerAlreadyRequested || (singleTicket && method !== 'cash'), fiscalMode: production ? 'verifactu' : 'legacy' }).catch((error) => options.onError('El cobro está guardado. ' + getReadableError(error, { operation: 'restaurant.action', step: 'postPaymentPrint' })))
        void refreshSales(result.ticketId, 'Pago completado sin imprimir', false).catch((error) => options.onError('El cobro está guardado. ' + getReadableError(error, { operation: 'restaurant.action', step: 'postPaymentRefresh' })))
        const nextMap = await realtime.loadCurrentMap(options.context, options.cashSession.id)
        realtime.setMap(nextMap)
        if (result.completed) {
          const nextOrder = result.nextOrderId ? await loadRestaurantOrder(options.context, result.nextOrderId) : null
          draft.replaceOrder(nextOrder)
          options.setAppliedDiscount(null)
          options.setMobileTicketOpen(false)
          setPosView(nextOrder
            ? { type: 'table_order', orderId: nextOrder.order.id }
            : { type: 'table_map', areaId: cleanedAreaId ?? nextMap.areas[0]?.id })
        }
      }
      return result
    } catch (error) {
      if (committedResult) {
        reportOperationError(error, { operation: 'restaurant.action', step: 'after_payment', recoverable: true })
        options.onError('El cobro está guardado. No se pudo completar la actualización o la impresión. Revisa el histórico; no vuelvas a cobrarlo.')
        return committedResult
      }
      options.onError(getReadableError(error, { operation: 'restaurant.action', cashSessionId: options.cashSession?.id, operationId: invoiceOrderId, step: 'payEqualSplitPart' }))
      throw error
    } finally {
      options.setBusy(false)
      paymentLockRef.current = false
    }
  }, [cleanupVirtualRoomTable, draft, equalSplit, options, realtime, refreshSales, invoiceOrderId])

  const paySelectedOrderItems = useCallback(async (
    moves: RestaurantOrderLineMove[],
    method: PaymentMethod | null,
    receivedCents: number | null,
    allowPending: boolean,
    discount: AppliedDiscount | null,
  ): Promise<PayRestaurantOrderItemsResult> => {
    assertRealSaleAllowed()
    const current = draft.getCurrentOrder()
    if (!options.context || !options.cashSession || !options.isOnline || !current) throw new UserFacingError('No hay una comanda abierta.')
    if (paymentLockRef.current) throw new UserFacingError('Ya hay un cobro en curso.')
    paymentLockRef.current = true
    options.setBusy(true)
    options.onError(null)
    let committedResult: PayRestaurantOrderItemsResult | null = null
    try {
      const saved = await draft.flush()
      if (!saved) throw new Error('No se pudo guardar la comanda antes del cobro.')
      const paymentLines = getMovedRestaurantPrintLines(saved.lines, moves)
      if (!allowPending) {
        const pending = await loadRestaurantOrderPendingUnits(options.context, saved.order.id)
        draft.replaceOrder(pending.detail)
        if (pending.pendingUnits > 0) return { requiresConfirmation: true, pendingUnits: pending.pendingUnits }
      }
      const amountCents = calculateDiscountForLines(paymentLines.map((line) => ({
        productId: line.productId ?? '', variantId: line.variantId ?? '', grossCents: line.lineTotalCents ?? line.unitPriceCents * line.quantity, quantity: line.quantity,
      })), discount).totalCents
      const payment = await payRestaurantSale<PayRestaurantOrderItemsResult>({
        context: options.context, session: options.cashSession, action: 'selected_items', target: saved.order.id, sequence: saved.order.revision,
        draft: buildRestaurantPrintPayload({ cashSession: options.cashSession, context: options.context, createdAt: nowIso(),
          discount, lines: paymentLines, paymentId: createId(), paymentMethod: method,
          receivedCents, changeCents: null, saleId: createId(), subtotalCents: getRestaurantPrintSubtotal(paymentLines),
          ticketId: createId(), totalCents: amountCents }),
        params: { orderId: saved.order.id, expectedRevision: saved.order.revision,
          moves: moves.map(move => ({ lineId: move.lineId, quantity: move.quantity })), method, receivedCents, allowPending,
          discount: withCalculationLines(discount, paymentLines) },
      })
      const result = payment.result
      if (!result.requiresConfirmation) committedResult = result
      const cashlogy = payment
      const prepared = { payload: payment.payload }
      const production = Boolean(payment.payload?.localFiscal)
      const cashDrawerAlreadyRequested = !result.requiresConfirmation && requestEarlyCashDrawer({ requestId: 'drawer:' + result.saleId + ':payment', payments: [{ method, amountCents }] })
      if (!result.requiresConfirmation) {
        if (production && !prepared?.payload) throw new Error('Falta la factura fiscal del cobro confirmado.')
        finishCashlogyPayment(cashlogy.transaction)
        const cleanedAreaId = selectionContainsAllOrderLines(saved.lines, moves)
          ? await cleanupVirtualRoomTable(saved, true)
          : null
        const printLines = paymentLines
        const [fiscal, ticketNumber] = await Promise.all([
          Promise.resolve(undefined),
          loadTicketNumberForPrint(options.context, options.cashSession, result.ticketId),
        ])
        void options.printSale(prepared?.payload ? { ...prepared.payload, ticket: { ...prepared.payload.ticket, ticketNumber } } : buildRestaurantPrintPayload({
          cashSession: options.cashSession,
          context: options.context,
          createdAt: nowIso(),
          discount,
          lines: printLines,
          paymentId: result.paymentId,
          paymentMethod: method,
          receivedCents: cashlogy.receivedCents,
          changeCents: cashlogy.changeCents,
          saleId: result.saleId,
          subtotalCents: result.subtotalCents,
          ticketId: result.ticketId,
          ticketNumber,
          totalCents: result.totalCents,
          fiscal,
        }), { cashDrawerAlreadyRequested, fiscalMode: production ? 'verifactu' : 'legacy' }).catch((error) => options.onError('El cobro está guardado. ' + getReadableError(error, { operation: 'restaurant.action', step: 'postPaymentPrint' })))
        void refreshSales(result.ticketId, 'Cobro completado sin imprimir', false).catch((error) => options.onError('El cobro está guardado. ' + getReadableError(error, { operation: 'restaurant.action', step: 'postPaymentRefresh' })))
        const [nextOrder, nextMap] = await Promise.all([
          cleanedAreaId ? Promise.resolve(null) : loadRestaurantOrder(options.context, saved.order.id),
          realtime.loadCurrentMap(options.context, options.cashSession.id),
        ])
        draft.replaceOrder(nextOrder)
        realtime.setMap(nextMap)
        options.setAppliedDiscount(null)
        options.setMobileTicketOpen(false)
        setSplitOrderGroup(null)
        setPosView(nextOrder
          ? { type: 'table_order', orderId: nextOrder.order.id }
          : { type: 'table_map', areaId: cleanedAreaId ?? nextMap.areas[0]?.id })
      }
      return result
    } catch (error) {
      if (committedResult) {
        reportOperationError(error, { operation: 'restaurant.action', step: 'after_payment', recoverable: true })
        options.onError('El cobro está guardado. No se pudo completar la actualización o la impresión. Revisa el histórico; no vuelvas a cobrarlo.')
        return committedResult
      }
      if (isRestaurantRevisionConflict(error)) {
        try {
          draft.replaceOrder(await loadRestaurantOrder(options.context, current.order.id))
          options.onError('La comanda cambió en otro dispositivo. Se ha recargado la versión más reciente.')
        } catch (reloadError) {
          options.onError(getReadableError(reloadError, { operation: 'restaurant.action', cashSessionId: options.cashSession?.id, operationId: invoiceOrderId, step: 'paySelectedOrderItems' }))
        }
      } else {
        options.onError(getReadableError(error, { operation: 'restaurant.action', cashSessionId: options.cashSession?.id, operationId: invoiceOrderId, step: 'paySelectedOrderItems' }))
      }
      throw error
    } finally {
      options.setBusy(false)
      paymentLockRef.current = false
    }
  }, [cleanupVirtualRoomTable, draft, options, realtime, refreshSales, invoiceOrderId])
  const splitOrder = useCallback(async (
    sourceOrderId: string,
    targetOrderId: string | null,
    moves: RestaurantOrderLineMove[],
  ): Promise<string | null> => {
    if (!options.context || !options.isOnline || !splitOrderGroup) return null
    const source = splitOrderGroup.orders.find((detail) => detail.order.id === sourceOrderId)
    const target = targetOrderId ? splitOrderGroup.orders.find((detail) => detail.order.id === targetOrderId) : null
    if (!source || (targetOrderId && !target)) return null
    options.setBusy(true)
    options.onError(null)
    try {
      const result = await moveRestaurantOrderLines(
        sourceOrderId,
        targetOrderId,
        source.order.revision,
        target?.order.revision ?? null,
        moves,
      )
      const refreshedGroup = await loadRestaurantOrderGroup(options.context, result.targetOrderId)
      setSplitOrderGroup(refreshedGroup)
      const currentId = draft.getCurrentOrder()?.order.id
      const nextId = result.sourceCancelled && currentId === sourceOrderId ? result.targetOrderId : currentId ?? result.targetOrderId
      const next = refreshedGroup.orders.find((detail) => detail.order.id === nextId && detail.order.status === 'open')
        ?? refreshedGroup.orders.find((detail) => detail.order.status === 'open')
      if (next) {
        draft.replaceOrder(next)
        setPosView({ type: 'table_order', orderId: next.order.id })
      }
      realtime.setMap(await realtime.loadCurrentMap(options.context))
      return result.targetOrderId
    } catch (error) {
      if (isRestaurantRevisionConflict(error)) {
        try {
          const currentId = draft.getCurrentOrder()?.order.id ?? sourceOrderId
          const group = await loadRestaurantOrderGroup(options.context, currentId)
          setSplitOrderGroup(group)
          const current = group.orders.find((detail) => detail.order.id === currentId && detail.order.status === 'open')
          if (current) draft.replaceOrder(current)
          options.onError('Las comandas cambiaron en otro dispositivo. Se ha recargado la versión más reciente.')
        } catch (reloadError) {
          options.onError(getReadableError(reloadError, { operation: 'restaurant.action', cashSessionId: options.cashSession?.id, operationId: invoiceOrderId, step: 'splitOrder' }))
        }
      } else {
        options.onError(getReadableError(error, { operation: 'restaurant.action', cashSessionId: options.cashSession?.id, operationId: invoiceOrderId, step: 'splitOrder' }))
      }
      return null
    } finally {
      options.setBusy(false)
    }
  }, [draft, options, realtime, splitOrderGroup, invoiceOrderId])

  const openOrderFromSplit = useCallback((orderId: string) => {
    const detail = splitOrderGroup?.orders.find((candidate) => candidate.order.id === orderId && candidate.order.status === 'open')
    if (!detail) return
    draft.replaceOrder(detail)
    options.setAppliedDiscount(null)
    options.setMobileTicketOpen(true)
    setPosView({ type: 'table_order', orderId })
    setSplitOrderGroup(null)
  }, [draft, options, splitOrderGroup])

  const requestPendingPaymentConfirmation = useCallback((method: PaymentMethod | null, receivedCents: number | null) => {
    const currentOrder = draft.getCurrentOrder()
    if (!options.context?.canTakePayments || !options.cashSession || !options.isOnline || paymentLockRef.current || !currentOrder) return false
    const pendingUnits = getOrderPendingUnits(currentOrder.lines)
    if (pendingUnits <= 0) return false
    setPendingPayment({ method, receivedCents, pendingUnits })
    return true
  }, [draft, options.context, options.cashSession, options.isOnline])

  const completePayment = useCallback(async (
    method: PaymentMethod | null,
    receivedCents: number | null,
    forceWithPending = false,
    confirmedCashlogyTransaction: CashlogyTransaction | null = null,
  ) => {
    const context = options.context
    const cashSession = options.cashSession
    try {
      assertRealSaleAllowed()
    } catch (error) {
      options.onError(error instanceof Error ? error.message : 'La emisión fiscal no está disponible.')
      return
    }
    if (!context?.canTakePayments || !cashSession || !draft.getCurrentOrder() || !options.isOnline || paymentLockRef.current) return
    if (!forceWithPending && requestPendingPaymentConfirmation(method, receivedCents)) return
    paymentLockRef.current = true
    options.setBusy(true)
    options.onError(null)
    let economicCommitted = false
    try {
      const saved = await draft.flush()
      if (!saved) return
      if (!forceWithPending) {
        const pendingCheck = await loadRestaurantOrderPendingUnits(context, saved.order.id)
        draft.replaceOrder(pendingCheck.detail)
        if (pendingCheck.pendingUnits > 0) {
          setPendingPayment({ method, receivedCents, pendingUnits: pendingCheck.pendingUnits })
          return
        }
      }
      setPaymentProcessing(true)
      const amountCents = calculateDiscountForLines(saved.lines.map((line) => ({
        productId: line.productId ?? '', variantId: line.variantId ?? '', grossCents: line.unitPriceCents * line.quantity, quantity: line.quantity,
      })), options.appliedDiscount).totalCents
      const payment = await payRestaurantSale<Awaited<ReturnType<typeof closeRestaurantOrder>>>({
        context, session: cashSession, action: 'close', target: saved.order.id,
        confirmedCashlogy: confirmedCashlogyTransaction ?? (forceWithPending ? pendingPayment?.cashlogyTransaction ?? null : null),
        draft: buildRestaurantPrintPayload({ cashSession, context, createdAt: nowIso(),
          discount: options.appliedDiscount, lines: saved.lines, paymentId: createId(), paymentMethod: method,
          receivedCents, changeCents: null, saleId: createId(), subtotalCents: getRestaurantPrintSubtotal(saved.lines),
          ticketId: createId(), totalCents: amountCents,
          invoice: invoiceCustomer ? { customerId: invoiceCustomer.id, customer: customerFiscalSnapshot(invoiceCustomer),
            series: null, number: null, issuedAt: null } : null }),
        params: { orderId: saved.order.id, expectedRevision: saved.order.revision, method, receivedCents, allowPending: forceWithPending,
          discount: withCalculationLines(options.appliedDiscount, saved.lines), customerId: invoiceCustomer?.id ?? null,
          customerSnapshot: invoiceCustomer ? customerFiscalSnapshot(invoiceCustomer) : null },
      })
      const result = payment.result
      const cashlogy = payment
      const prepared = { payload: payment.payload }
      const production = Boolean(payment.payload?.localFiscal)
      const cashDrawerAlreadyRequested = !result.requiresConfirmation && requestEarlyCashDrawer({ requestId: 'drawer:' + result.saleId + ':payment', payments: [{ method, amountCents }] })
      if (result.requiresConfirmation) {
        setPendingPayment({ method, receivedCents: cashlogy.receivedCents, pendingUnits: result.pendingUnits, cashlogyTransaction: cashlogy.transaction, cashDrawerAlreadyRequested })
        return
      }
      economicCommitted = true
      if (production && !prepared?.payload) throw new Error('Falta la factura fiscal del cobro confirmado.')
      finishCashlogyPayment(cashlogy.transaction)
      const nextOrder = result.nextOrderId ? await loadRestaurantOrder(context, result.nextOrderId) : null
      const returnAreaId = getVirtualRoomTable(saved)?.areaId
        ?? saved.tables[0]?.areaId
        ?? realtime.map.areas[0]?.id
      if (!nextOrder) {
        const releasedTableIds = new Set(saved.tables.map((table) => table.id))
        realtime.setMap((current) => ({
          ...current,
          tables: current.tables
            .filter((table) => !releasedTableIds.has(table.id) || !table.isVirtual)
            .map((table) => releasedTableIds.has(table.id) ? {
              ...table,
              status: table.nextReservation ? 'reserved' : 'free',
              orderId: null,
              orderOpenedAt: null,
              guestCount: null,
              totalCents: 0,
              pendingUnits: 0,
              readyUnits: 0,
              groupTableIds: [],
            } : table),
        }))
      }
      draft.replaceOrder(nextOrder)
      setPendingPayment(null)
      options.setMobileTicketOpen(false)
      options.setAppliedDiscount(null)
      setInvoiceCustomer(null)
      options.onPaidFeedback(method)
      setPosView(nextOrder
        ? { type: 'table_order', orderId: nextOrder.order.id }
        : { type: 'table_map', areaId: returnAreaId })
      window.setTimeout(() => options.onPaidFeedback(null), 500)

      const refreshMapTask = (async () => {
        await cleanupVirtualRoomTable(saved, true)
        // The committed result already updates these tables locally. Realtime
        // reconciles other terminals; do not issue a second full-map refresh here.
      })()
      const refreshSalesTask = Promise.all([
        options.syncPendingEvents(),
        refreshSales(result.ticketId, 'Cobro completado sin imprimir', false),
      ])
      const printTask = (async () => {
        const [fiscal, invoice, ticketNumber] = await Promise.all([
          Promise.resolve(undefined),
          production ? Promise.resolve(prepared?.payload?.ticket.invoice ?? null)
            : invoiceCustomer ? loadTicketInvoice(context.tenantId, result.ticketId) : Promise.resolve(null),
          loadTicketNumberForPrint(context, cashSession, result.ticketId),
        ])
        if (invoiceCustomer && !invoice) {
          throw new Error('El cobro se ha registrado, pero no se ha confirmado el número de factura. No se imprimirá como ticket normal.')
        }
        await options.printSale(prepared?.payload ? { ...prepared.payload, ticket: { ...prepared.payload.ticket, ticketNumber } } : buildRestaurantPrintPayload({
          cashSession,
          context,
          createdAt: nowIso(),
          discount: options.appliedDiscount,
          lines: saved.lines,
          paymentId: result.paymentId,
          paymentMethod: method,
          receivedCents: cashlogy.receivedCents,
          changeCents: cashlogy.changeCents,
          saleId: result.saleId,
           subtotalCents: getRestaurantPrintSubtotal(saved.lines),
           ticketId: result.ticketId,
           ticketNumber,
           totalCents: result.totalCents,
           fiscal,
          invoice,
        }), { cashDrawerAlreadyRequested, fiscalMode: production ? 'verifactu' : 'legacy' })
      })()
      void Promise.allSettled([refreshMapTask, refreshSalesTask, printTask]).then((tasks) => {
        const failures = tasks
          .filter((task): task is PromiseRejectedResult => task.status === 'rejected')
          .map((task) => getReadableError(task.reason, { operation: 'restaurant.action', cashSessionId: cashSession.id, operationId: saved.order.id, step: 'completePayment' }))
        if (failures.length > 0 && paymentScopeRef.current === paymentScope) options.onError('El cobro está guardado. ' + failures.join(' '))
      })
    } catch (error) {
      options.onError(economicCommitted
        ? 'El cobro está guardado. No se pudo completar la actualización o la impresión. Revisa el histórico; no vuelvas a cobrarlo.'
        : getReadableError(error, { operation: 'restaurant.action', cashSessionId: options.cashSession?.id, operationId: invoiceOrderId, step: 'completePayment' }))
    } finally {
      setPaymentProcessing(false)
      options.setBusy(false)
      paymentLockRef.current = false
    }
  }, [cleanupVirtualRoomTable, draft, invoiceCustomer, options, paymentScope, pendingPayment, realtime, refreshSales, requestPendingPaymentConfirmation, invoiceOrderId])

  const requestCloseCash = useCallback(async () => {
    if (!options.context || !options.cashSession) return false
    if (draft.getCurrentOrder() && !await draft.flush()) return false
    const closureError = await getRestaurantCashClosureError({
      cashSession: options.cashSession,
      context: options.context,
      isOnline: options.isOnline,
      tablesEnabled: realtime.tablesEnabled,
    })
    if (!closureError) return true
    options.onError(closureError)
    return false
  }, [draft, options, realtime.tablesEnabled])

  const addLine = useCallback((
    sellable: ResolvedSellableProduct,
    selection: ProductLineSelection,
    item: ResolvedCatalogItem | null,
    lineId?: string,
    sourceElement?: HTMLElement | null,
  ) => {
    if (!options.isOnline) {
      options.onError('La gestión de mesas requiere conexión.')
      return false
    }
    const current = draft.getCurrentOrder()
    if (!current || !options.context || !options.catalog) return false
    const candidate = buildSaleLine(createId(), options.catalog, sellable, selection, item, true)
    if (lineId) {
      const previous = current.lines.find((line) => line.id === lineId)
      if (!previous) return false
      const isMenu = sellable.product.type === 'menu'
      let menuEdit: ReturnType<typeof reconcileMenuEdit> | null = null
      try {
        if (isMenu) menuEdit = reconcileMenuEdit(previous, candidate.components, productionState?.entries.filter((entry) => entry.lineId === lineId) ?? [], candidate.modifiers)
      } catch (error) {
        options.onError(getReadableError(error, { operation: 'restaurant.menu.edit' }))
        return false
      }
      if ((menuEdit ? menuEdit.changesSentDish : (productionState?.lines.find((line) => line.lineId === lineId)?.sentQuantity ?? 0) > 0)
        && !window.confirm('Este producto ya se envió a producción. Se notificará la modificación a cocina/barra. ¿Continuar?')) return false
      const timestamp = nowIso()
      draft.updateDraft((detail) => ({
        ...detail,
        lines: detail.lines.map((line) => line.id === lineId ? {
          ...line,
          productId: candidate.productId,
          variantId: candidate.variantId,
          productName: candidate.productName,
          variantName: candidate.variantName,
          unitPriceCents: menuEdit?.unitPriceCents ?? candidate.unitPriceCents,
          modifiers: candidate.modifiers,
          components: menuEdit?.components ?? candidate.components,
          catalogSnapshot: isMenu ? line.catalogSnapshot : candidate.catalogSnapshot,
          mixerProductId: candidate.mixerProductId ?? null,
          mixer: candidate.mixer ?? null,
          updatedAt: timestamp,
        } : line),
      }))
      options.onAddFeedback({ feedbackType: 'updated', productName: candidate.productName, sourceElement })
      return true
    }
    const signature = getLineSignature(candidate)
    const existing = sellable.product.type === 'menu' ? undefined : current.lines.find((line) => line.productId !== null
      && line.note === null
      && getLineSignature({
        productId: line.productId,
        variantId: line.variantId ?? '',
        modifiers: line.modifiers,
        components: line.components,
        mixerProductId: line.mixerProductId,
      }) === signature)
    const timestamp = nowIso()
    draft.updateDraft((detail) => ({
      ...detail,
      lines: existing
        ? detail.lines.map((line) => line.id === existing.id ? { ...line, quantity: line.quantity + 1, updatedAt: timestamp } : line)
        : [...detail.lines, {
            id: candidate.id,
            tenantId: options.context!.tenantId,
            venueId: options.context!.venueId,
            orderId: detail.order.id,
            productId: candidate.productId,
            variantId: candidate.variantId,
            productName: candidate.productName,
            variantName: candidate.variantName,
            unitPriceCents: candidate.unitPriceCents,
            quantity: 1,
            servedQuantity: 0,
            fullyServedAt: null,
            modifiers: candidate.modifiers,
            components: candidate.components,
            catalogSnapshot: candidate.catalogSnapshot,
            mixerProductId: candidate.mixerProductId ?? null,
            mixer: candidate.mixer ?? null,
            note: null,
            createdAt: timestamp,
            updatedAt: timestamp,
          }],
    }))
    options.onAddFeedback({ feedbackType: 'added', productName: candidate.productName, sourceElement })
    return true
  }, [draft, options, productionState])

  const addMenuBatch = useCallback((sellable: ResolvedSellableProduct, selections: ProductLineSelection[], item: ResolvedCatalogItem, sourceElement?: HTMLElement | null) => {
    const { context, catalog } = options
    const current = draft.getCurrentOrder()
    if (!options.isOnline || options.isBusy || !context?.canTakeOrders || !catalog || !current || current.order.status !== 'open') return false
    if (catalog.tenantId !== context.tenantId || catalog.venueId !== context.venueId) return false
    try {
      const lines = buildRestaurantMenuBatchLines({ catalog, sellable, selections, item, context, orderId: current.order.id, createLineId: createId, timestamp: nowIso() })
      draft.updateDraft((detail) => ({ ...detail, lines: [...detail.lines, ...lines] }))
      options.onAddFeedback({ feedbackType: 'added', productName: `${lines.length} × ${sellable.product.name}`, sourceElement })
      return true
    } catch (error) {
      options.onError(getReadableError(error, { operation: 'restaurant.menu.batch' }))
      return false
    }
  }, [draft, options])

  const changeLineQuantity = useCallback((lineId: string, direction: 1 | -1) => {
    if (!options.isOnline) return
    const line = draft.getCurrentOrder()?.lines.find((item) => item.id === lineId)
    if (!line) return
    if (direction === 1 && (line.catalogSnapshot.productType === 'menu' || line.components.some((component) => component.type === 'menu_component'))) {
      const timestamp = nowIso()
      draft.updateDraft((detail) => ({ ...detail, lines: [...detail.lines, { ...line, id: createId(), quantity: 1, servedQuantity: 0,
        fullyServedAt: null, createdAt: timestamp, updatedAt: timestamp }] }))
      return
    }
    const servedQuantity = Math.max(line.servedQuantity, ...(productionState?.entries.filter((entry) => entry.lineId === lineId).map((entry) => entry.servedQuantity ?? 0) ?? []))
    if (direction === -1 && line.quantity - 1 < servedQuantity) {
      if (line.quantity === 1) setPendingLineRemoval(line)
      else options.onError('No puedes reducir la cantidad por debajo de los platos servidos.')
      return
    }
    if (direction === -1
      && (productionState?.lines.find((state) => state.lineId === lineId)?.sentQuantity ?? 0) > line.quantity - 1
      && !window.confirm('Esta unidad ya se envió a producción. Se generará una anulación para cocina/barra. ¿Continuar?')) return
    if (direction === -1 && !canDecreaseLineQuantity(line)) {
      options.onError('No puedes reducir la cantidad por debajo de las unidades servidas.')
      return
    }
    draft.updateDraft((detail) => ({
      ...detail,
      lines: detail.lines
        .map((item) => item.id === lineId ? { ...item, quantity: item.quantity + direction, updatedAt: nowIso() } : item)
        .filter((item) => item.quantity > 0),
    }))
  }, [draft, options, productionState])

  const setLineQuantity = useCallback((lineId: string, quantity: number) => {
    if (!options.isOnline) return
    const line = draft.getCurrentOrder()?.lines.find((item) => item.id === lineId)
    if (!line || line.quantity === quantity) return
    const normalizedQuantity = roundQuantity(quantity)
    if (!isValidQuantity(quantity) || normalizedQuantity < line.servedQuantity || normalizedQuantity < 1) {
      options.onError('La cantidad debe ser positiva, tener como máximo tres decimales y no ser inferior a las unidades servidas.')
      return
    }
    if (normalizedQuantity < line.quantity
      && (productionState?.lines.find((state) => state.lineId === lineId)?.sentQuantity ?? 0) > normalizedQuantity
      && !window.confirm('Parte de esta cantidad ya se envió a producción. Se generará una anulación para cocina/barra. ¿Continuar?')) return
    draft.updateDraft((detail) => ({
      ...detail,
      lines: detail.lines.map((item) => item.id === lineId ? { ...item, quantity: normalizedQuantity, updatedAt: nowIso() } : item),
    }))
  }, [draft, options, productionState])

  const setLineUnitPrice = useCallback((lineId: string, unitPriceCents: number) => {
    if (!options.isOnline) return
    if (!Number.isSafeInteger(unitPriceCents) || unitPriceCents < 0) {
      options.onError('El precio unitario no es válido.')
      return
    }
    const line = draft.getCurrentOrder()?.lines.find((item) => item.id === lineId)
    if (!line || line.unitPriceCents === unitPriceCents) return
    draft.updateDraft((detail) => ({
      ...detail,
      lines: detail.lines.map((item) => item.id === lineId ? { ...item, unitPriceCents, updatedAt: nowIso() } : item),
    }))
  }, [draft, options])

  const setLineNote = useCallback(async (lineId: string, value: string) => {
    if (!options.context?.canTakeOrders || !options.isOnline || options.isBusy) return false
    const current = draft.getCurrentOrder()
    const line = current?.lines.find((item) => item.id === lineId)
    if (!current || current.order.status !== 'open' || !line) return false
    const note = Array.from(value.replace(/\r\n?/g, '\n'))
      .filter((character) => character === '\n' || character === '\t' || (character.charCodeAt(0) >= 32 && character.charCodeAt(0) !== 127))
      .join('').trim() || null
    if (line.note !== note) draft.updateDraft((detail) => ({
      ...detail,
      lines: detail.lines.map((item) => item.id === lineId ? { ...item, note, updatedAt: nowIso() } : item),
    }))
    const saved = await draft.flush()
    return saved?.order.id === current.order.id && saved.lines.some((item) => item.id === lineId && item.note === note)
  }, [draft, options])

  const runServiceAction = useCallback((action: (order: RestaurantOrderDetail) => Promise<void>) => runBusy(async () => {
    if (!options.context || !options.isOnline) return
    const saved = await draft.flush()
    if (!saved) return
    try {
      await action(saved)
    } finally {
      draft.replaceOrder(await loadRestaurantOrder(options.context, saved.order.id))
    }
  }), [draft, options, runBusy])

  const changeProductionPass = useCallback((input: { lineId: string; componentId?: string | null; passId: string }) => runBusy(async () => {
    if (!options.context || !options.isOnline || !productionAvailable) return
    const saved = await draft.flush()
    if (!saved) return
    await setOrderLineProductionPass(input)
    setProductionState(await loadOrderProductionState(saved.order.id))
  }), [draft, options.context, options.isOnline, productionAvailable, runBusy])

  const serveComponent = useCallback((lineId: string, componentId: string, quantity: number) => runBusy(async () => {
    if (!options.context || !options.isOnline) return
    const saved = await draft.flush()
    if (!saved) return
    try {
      await serveMenuComponent({ orderId: saved.order.id, lineId, componentId, quantity,
        expectedRevision: saved.order.revision, deviceId: options.context.deviceId })
    } finally {
      draft.replaceOrder(await loadRestaurantOrder(options.context, saved.order.id))
      await refreshProduction(saved.order.id)
    }
  }), [draft, options.context, options.isOnline, refreshProduction, runBusy])

  const sendToProduction = useCallback((selection?: ProductionSelection[]) => runBusy(async () => {
    if (!options.context || !options.isOnline || !productionAvailable) return
    const saved = await draft.flush()
    if (!saved) return
    const authoritative = await loadOrderProductionState(saved.order.id)
    const authoritativeByKey = new Map(authoritative.entries.map((entry) => [`${entry.lineId}:${entry.componentId ?? ''}`, entry]))
    const reconciledSelection = selection?.flatMap((entry) => {
      const serverEntry = authoritativeByKey.get(`${entry.lineId}:${entry.componentId ?? ''}`)
      if (!serverEntry?.hasProductionDestination) return []
      return [{ ...entry, quantity: Math.min(entry.quantity, serverEntry.unsentQuantity), passId: serverEntry.passId, passName: serverEntry.passName }]
    }).filter((entry) => entry.quantity > 0)
    const routableEntries = authoritative.entries
      .filter((entry) => entry.hasProductionDestination && entry.unsentQuantity > 0)
      .map((entry) => ({
        lineId: entry.lineId,
        componentId: entry.componentId,
        quantity: entry.unsentQuantity,
        passId: entry.passId,
        passName: entry.passName,
      }))
    const effectiveSelection = selection ? (reconciledSelection ?? []) : routableEntries
    if (effectiveSelection.length === 0) {
      setProductionState(authoritative)
      return
    }
    const selections = [...effectiveSelection.reduce((groups, entry) => {
      const passId = entry.passId ?? ''
      groups.set(passId, [...(groups.get(passId) ?? []), entry])
      return groups
    }, new Map<string, ProductionSelection[]>()).values()]
    for (const reconciledPassSelection of selections) {
      await sendProductionBatch({
        orderId: saved.order.id,
        expectedRevision: saved.order.revision,
        deviceId: options.context.deviceId,
        requestId: `pos:${options.context.deviceId}:${createId()}`,
        selection: reconciledPassSelection,
      })
    }
    setProductionState(await loadOrderProductionState(saved.order.id))
  }), [draft, options.context, options.isOnline, productionAvailable, refreshProduction, runBusy])

  const updateVenueLayout = useCallback(async (
    expectedRevision: number,
    tables: Parameters<typeof saveVenueTableLayout>[2],
  ) => {
    if (!options.context) throw new UserFacingError('No hay un local seleccionado.')
    options.setBusy(true)
    try {
      const saved = await saveVenueTableLayout(options.context, expectedRevision, tables)
      realtime.setMap((current) => applyVenueLayout(current, saved))
      return saved
    } finally {
      options.setBusy(false)
    }
  }, [options, realtime])

  const restoreVenueLayout = useCallback(async (expectedRevision: number) => {
    if (!options.context) throw new UserFacingError('No hay un local seleccionado.')
    options.setBusy(true)
    try {
      const saved = await restoreVenueTableLayout(options.context, expectedRevision)
      realtime.setMap((current) => applyVenueLayout(current, saved))
      return saved
    } finally {
      options.setBusy(false)
    }
  }, [options, realtime])

  const reset = useCallback((areaId?: string) => {
    draft.clearOrder()
    setPosView({ type: 'quick_sale', areaId })
    setMoveOrderId(null)
    setPendingPayment(null)
    setPendingLineRemoval(null)
    setSplitOrderGroup(null)
    setEqualSplitOpen(false)
    setEqualSplit(null)
    setInvoiceCustomer(null)
  }, [draft])

  return {
    addLine,
    addMenuBatch,
    cancelEmptyOrder,
    changeLineQuantity,
    changeProductionPass,
    clearTicket: () => draft.updateDraft((detail) => ({ ...detail, lines: [] })),
    completePayment,
    configureEqualSplit,
    createVirtualTable,
    createVirtualTableFromQuickSale,
    deleteVirtualTable,
    confirmLineRemoval,
    equalSplit,
    equalSplitOpen,
    invoiceCustomer,
    map: realtime.map,
    moveOrder,
    moveOrderId,
    openEqualSplitOrder,
    openExistingOrder,
    openOrderFromSplit,
    openSplitOrder,
    openTableOrder,
    order: draft.order,
    payEqualSplitPart,
    paySelectedOrderItems,
    pendingLineRemoval,
    pendingPayment,
    paymentProcessing,
    requestPendingPaymentConfirmation,
    posView,
    prepareMove,
    requestCloseCash,
    requestLineRemoval,
    removeInvoiceCustomer: () => setInvoiceCustomer(null),
    reset,
    returnToMap,
    saveState: draft.saveState,
    saveQuickSaleToExistingTable,
    productionState,
    sendToProduction,
    serveLineFully: (lineId: string) => void runServiceAction(() => markRestaurantOrderLineFullyServed(lineId)),
    serveComponent,
    serveLineUnit: (lineId: string) => void runServiceAction(() => markRestaurantOrderLineUnitsServed(lineId, 1)),
    serveOrderFully: () => void runServiceAction((order) => markRestaurantOrderFullyServed(order.order.id)),
    setEqualSplit,
    setEqualSplitOpen,
    setInvoiceCustomer,
    setLineQuantity,
    setLineNote,
    setLineUnitPrice,
    setMap: realtime.setMap,
    setMoveOrderId,
    setPendingLineRemoval,
    setPendingPayment,
    setPosView,
    setSplitOrderGroup,
    splitOrder,
    splitOrderGroup,
    tablesConfigLoaded: realtime.configLoaded,
    tablesEnabled: realtime.tablesEnabled,
    updateDraft: draft.updateDraft,
    updateVenueLayout,
    restoreVenueLayout,
    reloadMap: realtime.refreshMap,
  }
}
